import apiClient from '@/lib/api-client';

export interface Business {
    id: string;
    name: string;
    shortCode: string;
    type: string;
    description: string | null;
    logoUrl: string | null;
    brandColor: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    website: string | null;
    address: string | null;
    isActive: boolean;
    branchCount: number;
    staffCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface BusinessListResponse {
    businesses: Business[];
    totalCount: number;
    hasNextPage: boolean;
}

export async function getBusinesses(
    params: { limit?: number; page?: number; search?: string; type?: string; sortBy?: string; order?: string } = {}
): Promise<BusinessListResponse> {
    const response = await apiClient.get('/businesses', { params });
    const d = response.data;

    // Handle flat array response: { success: true, data: [...] }
    if (Array.isArray(d?.data)) {
        const businesses = d.data.map((item: any) => ({
            id: item.id || '',
            name: item.name || '',
            shortCode: item.shortCode || '',
            type: item.type || '',
            description: item.description || null,
            logoUrl: item.logoUrl || null,
            brandColor: item.brandColor || null,
            contactEmail: item.contactEmail || null,
            contactPhone: item.contactPhone || null,
            website: item.website || null,
            address: item.address || null,
            isActive: item.isActive !== false,
            branchCount: item.branchCount || 0,
            staffCount: item.staffCount || 0,
            createdAt: item.createdAt || new Date().toISOString(),
            updatedAt: item.updatedAt || new Date().toISOString(),
        }));
        return {
            businesses,
            totalCount: businesses.length,
            hasNextPage: false,
        };
    }

    // Handle paginated response
    if (d?.data?.businesses) {
        return {
            businesses: d.data.businesses,
            totalCount: d.data.totalCount || d.meta?.pagination?.totalCount || 0,
            hasNextPage: d.data.hasNextPage || d.meta?.pagination?.hasNextPage || false,
        };
    }

    return { businesses: [], totalCount: 0, hasNextPage: false };
}

export async function getBusinessesWithStats(): Promise<Business[]> {
    try {
        const response = await apiClient.get('/businesses/stats');
        const d = response.data;
        if (Array.isArray(d?.data)) return d.data;
        return [];
    } catch (err) {
        // Silently fail for stats - not critical
        console.warn('Failed to load business stats:', err);
        return [];
    }
}

export async function getBusinessById(id: string): Promise<Business> {
    const response = await apiClient.get(`/businesses/${id}`);
    const item = response.data?.data;
    return {
        id: item?.id || '',
        name: item?.name || '',
        shortCode: item?.shortCode || '',
        type: item?.type || '',
        description: item?.description || null,
        logoUrl: item?.logoUrl || null,
        brandColor: item?.brandColor || null,
        contactEmail: item?.contactEmail || null,
        contactPhone: item?.contactPhone || null,
        website: item?.website || null,
        address: item?.address || null,
        isActive: item?.isActive !== false,
        branchCount: item?.branchCount || 0,
        staffCount: item?.staffCount || 0,
        createdAt: item?.createdAt || new Date().toISOString(),
        updatedAt: item?.updatedAt || new Date().toISOString(),
    };
}

export async function createBusiness(payload: {
    name: string; shortCode: string; type: string; description?: string;
    brandColor?: string; contactEmail?: string; contactPhone?: string; website?: string; address?: string;
}): Promise<Business> {
    // ✅ Only send required fields + non-empty optional fields
    const cleanPayload: Record<string, unknown> = {
        name: payload.name.trim(),
        shortCode: payload.shortCode.toUpperCase().trim(),
        type: payload.type,
    };

    // Add optional fields only if they have actual values
    if (payload.description?.trim()) cleanPayload.description = payload.description.trim();
    if (payload.contactEmail?.trim()) cleanPayload.contactEmail = payload.contactEmail.trim();
    if (payload.contactPhone?.trim()) cleanPayload.contactPhone = payload.contactPhone.trim();
    if (payload.address?.trim()) cleanPayload.address = payload.address.trim();
    if (payload.brandColor?.trim()) cleanPayload.brandColor = payload.brandColor.trim();

    // ✅ Format website URL if provided
    if (payload.website?.trim()) {
        let website = payload.website.trim();
        // Add https:// if no protocol is specified
        if (!/^https?:\/\//i.test(website)) {
            website = 'https://' + website;
        }
        cleanPayload.website = website;
    }

    console.log('📤 Sending create payload:', JSON.stringify(cleanPayload, null, 2));

    try {
        const response = await apiClient.post('/businesses', cleanPayload);
        const item = response.data?.data;
        return {
            id: item?.id || '',
            name: item?.name || cleanPayload.name,
            shortCode: item?.shortCode || cleanPayload.shortCode,
            type: item?.type || cleanPayload.type,
            description: item?.description || null,
            logoUrl: item?.logoUrl || null,
            brandColor: item?.brandColor || null,
            contactEmail: item?.contactEmail || null,
            contactPhone: item?.contactPhone || null,
            website: item?.website || null,
            address: item?.address || null,
            isActive: item?.isActive !== false,
            branchCount: item?.branchCount || 0,
            staffCount: item?.staffCount || 0,
            createdAt: item?.createdAt || new Date().toISOString(),
            updatedAt: item?.updatedAt || new Date().toISOString(),
        };
    } catch (err: any) {
        // ✅ Log the full error for debugging
        console.error('❌ Create business failed:', {
            status: err?.response?.status,
            statusText: err?.response?.statusText,
            data: err?.response?.data,
            message: err?.message,
        });
        // Re-throw so the mutation's onError can handle it
        throw err;
    }
}

export async function updateBusiness(id: string, payload: Record<string, unknown>): Promise<Business> {
    const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    console.log('🔍 PATCH payload being sent:', JSON.stringify(cleanPayload));
    const response = await apiClient.patch(`/businesses/${id}`, cleanPayload);
    const item = response.data?.data;
    return {
        id: item?.id || id,
        name: item?.name || '',
        shortCode: item?.shortCode || '',
        type: item?.type || '',
        description: item?.description || null,
        logoUrl: item?.logoUrl || null,
        brandColor: item?.brandColor || null,
        contactEmail: item?.contactEmail || null,
        contactPhone: item?.contactPhone || null,
        website: item?.website || null,
        address: item?.address || null,
        isActive: item?.isActive !== false,
        branchCount: item?.branchCount || 0,
        staffCount: item?.staffCount || 0,
        createdAt: item?.createdAt || new Date().toISOString(),
        updatedAt: item?.updatedAt || new Date().toISOString(),
    };
}

export async function deactivateBusiness(id: string): Promise<void> {
    await apiClient.delete(`/businesses/${id}`);
}